import { useState, useCallback, useEffect } from 'react';
import geminiService from '../services/geminiService';
import serverStorageService from '../services/serverStorageService';

export interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  timestamp: Date;
}

export const useImageGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);

  // Load saved images on mount (single-player mode, no polling)
  useEffect(() => {
    const loadImages = async () => {
      const savedImages = await serverStorageService.getAllImages();
      if (savedImages.length > 0) {
        setGeneratedImages(savedImages);
        console.log(`Loaded ${savedImages.length} saved stickers from localStorage`);
      }
    };

    loadImages();
    // No polling needed in single-player mode
  }, []);

  const generateFromPrompt = useCallback(async (
    prompt: string,
    refImages: string[] = []
  ) => {
    setIsGenerating(true);
    setError(null);

    try {
      const response = await geminiService.generateImages(prompt, refImages);

      if (response.status === 'completed' && response.images.length > 0) {
        const newImages: GeneratedImage[] = response.images.map((url, index) => ({
          id: `${response.id}-${index}`,
          url,
          prompt,
          timestamp: new Date()
        }));

        // Save new images to localStorage
        await serverStorageService.saveImages(newImages);

        // Update state with new images at the beginning
        setGeneratedImages(prev => [...newImages, ...prev]);

        return newImages;
      } else {
        throw new Error(response.error || 'Failed to generate images');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate images';
      setError(errorMessage);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const clearImages = useCallback(async () => {
    await serverStorageService.clearAllImages();
    setGeneratedImages([]);
  }, []);

  const deleteImage = useCallback(async (imageId: string) => {
    await serverStorageService.deleteImage(imageId);
    setGeneratedImages(prev => prev.filter(img => img.id !== imageId));
  }, []);

  return {
    generatedImages,
    isGenerating,
    error,
    generateFromPrompt,
    clearImages,
    deleteImage,
    imageCount: generatedImages.length
  };
};